import { env } from '../config/env';

const API_BASE_URL = env.API_BASE_URL;

const getMockAliasCount = () => {
  if (!env.DEV) return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('mockAliasCount') || localStorage.getItem('uumail_mock_alias_count');
  if (!raw) return null;
  const count = Number.parseInt(raw, 10);
  if (!Number.isFinite(count) || count < 0) return null;
  localStorage.setItem('uumail_mock_alias_count', String(count));
  return count;
};

const makeMockAliases = (count: number) => {
  return Array.from({ length: count }, (_, index) => ({
    alias: `service${index + 1}`,
    enable: true,
    time: Math.floor(Date.now() / 1000) - index * 86400,
    fwd: Math.max(0, 24 - (index % 25)),
  }));
};

const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

const PUBLIC_STATS_CACHE_KEY = 'uumail_public_stats';

type PublicStatsCache = {
  data: { aliasCount: number; updatedAt?: number; nextUpdateAt?: number };
  expiresAt: number;
};

export const fetchPublicStats = async () => {
  const now = Date.now();
  try {
    const cached = localStorage.getItem(PUBLIC_STATS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as PublicStatsCache;
      if (parsed?.data?.aliasCount >= 0 && parsed.expiresAt > now) {
        return { code: 0, result: parsed.data };
      }
    }
  } catch {
    localStorage.removeItem(PUBLIC_STATS_CACHE_KEY);
  }

  const response = await fetch(`${API_BASE_URL}/pub/stats`, {
    headers: getHeaders(),
  });
  const data = await response.json();

  if (data.code === 0 && Number.isFinite(data.result?.aliasCount)) {
    const expiresAt = Number.isFinite(data.result.nextUpdateAt)
      ? data.result.nextUpdateAt
      : now + 24 * 60 * 60 * 1000;
    localStorage.setItem(PUBLIC_STATS_CACHE_KEY, JSON.stringify({ data: data.result, expiresAt }));
  }

  return data;
};
//test
export const fetchUserInfo = async () => {
  const mockAliasCount = getMockAliasCount();
  if (mockAliasCount !== null) {
    return {
      code: 0,
      result: {
        uid: 1000000001,
        name: 'mockuser',
        domain: 'uu.me',
        addr: 'mock@example.com',
        level: 'free',
        plan: { name: 'free', endTime: 0 },
        aliasCount: mockAliasCount,
        fwd: mockAliasCount * 3,
        rpy: Math.floor(mockAliasCount / 2),
        pin: '00',
        curMonth: `${new Date().getFullYear()}-${new Date().getMonth() + 1}`,
        month_total: Math.min(mockAliasCount * 2, 300),
        limits: {
          aliasLimits: 3,
          dailylimits: 10,
          monthlyLimits: 300,
        },
      },
    };
  }
  const response = await fetch(`${API_BASE_URL}/v1/user/info`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};

let cachedDailyStats: { data: any, timestamp: number } | null = null;

export const fetchDailySendStats = async ({ force = false } = {}) => {
  const mockAliasCount = getMockAliasCount();
  if (mockAliasCount !== null) {
    return { code: 0, result: { times: { main_total: Math.min(mockAliasCount, 10) } } };
  }
  const now = Date.now();
  if (!force && cachedDailyStats && (now - cachedDailyStats.timestamp < 120000)) {
    return cachedDailyStats.data;
  }
  
  const response = await fetch(`${API_BASE_URL}/v1/addr/getdailysend`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  const data = await response.json();
  
  if (data.code === 0) {
    cachedDailyStats = {
      data,
      timestamp: now
    };
  }
  
  return data;
};

export const fetchAliasList = async (params = { skip: 0, limit: 200 }) => {
  const mockAliasCount = getMockAliasCount();
  if (mockAliasCount !== null) {
    const skip = params.skip || 0;
    const limit = params.limit || 200;
    return {
      code: 0,
      result: {
        total: mockAliasCount,
        names: makeMockAliases(mockAliasCount).slice(skip, skip + limit),
      },
    };
  }
  const queryParams = new URLSearchParams(params as any).toString();
  const response = await fetch(`${API_BASE_URL}/v1/addr/list?${queryParams}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};

export const fetchVipStatus = async () => {
  const response = await fetch(`${API_BASE_URL}/v1/user/isvip`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};

export const fetchActivities = async (limit = 10, skip = 0) => {
  const mockAliasCount = getMockAliasCount();
  if (mockAliasCount !== null) {
    return {
      code: 0,
      result: makeMockAliases(Math.min(mockAliasCount, limit)).map((alias, index) => ({
        occurred_at: new Date(Date.now() - index * 3600000).toISOString(),
        event_type: 'forwarded_success',
        direction: 'inbound',
        alias_email: `${alias.alias}@mockuser.uu.me`,
        sender_name: 'Mock Sender',
        sender_email: `sender${index + 1}@example.com`,
        delivered_to_email: 'mock@example.com',
        meta: { mock: true },
      })),
    };
  }
  const response = await fetch(`${API_BASE_URL}/v1/user/activities?limit=${limit}&skip=${skip}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};

export const updateUserSettings = async (data: { name?: string, real?: string; pin?: string; send_pin?: string }) => {
  const response = await fetch(`${API_BASE_URL}/v1/user/update`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(data),
  });
  return response.json();
};

export const deleteAccount = async (confirm: string) => {
  const response = await fetch(`${API_BASE_URL}/v1/user/delete`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ confirm }),
  });
  return response.json();
};

export const updateAliasStatus = async (alias: string, status: 'enable' | 'disable') => {
  const response = await fetch(`${API_BASE_URL}/v1/addr/setStatus`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ alias, status }),
  });
  return response.json();
};

export const deleteAliases = async (aliases: string) => {
  const response = await fetch(`${API_BASE_URL}/v1/addr/delete`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ aliases }),
  });
  return response.json();
};

type AliasCreateAttribution = {
  from?: 'aliases_page' | 'sidebar';
  flowId?: string;
  sessionId?: string;
};

export const createAlias = async (alias: string, attribution: AliasCreateAttribution = {}) => {
  const response = await fetch(`${API_BASE_URL}/v1/addr/update`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ alias, ...attribution }),
  });
  return response.json();
};

export const fetchPlans = async () => {
  const response = await fetch(`${API_BASE_URL}/pay/plans`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};

export const createPayment = async (plan: any, campaign?: string) => {
  const response = await fetch(`${API_BASE_URL}/pay/createPaymentUrl`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      plan: plan.id,
      success_url: 'https://mail.uu.me/dashboard',
      cancel_url: 'https://mail.uu.me/dashboard',
      lang: localStorage.getItem('i18nextLng') == 'en'?'en':'zh',
      ...(campaign ? { campaign } : {})
    }),
  });
  return response.json();
};

export type FeedbackPayload = {
  content: string;
  metadata?: Record<string, string | number | boolean>;
};

export const collectFeedback = async (payload: FeedbackPayload) => {
  const response = await fetch(`${API_BASE_URL}/v1/feedback/collect`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok || data.err) throw new Error(data.err || 'feedback-unavailable');
  return data;
};

export const cancelPlan = async () => {
  const response = await fetch(`${API_BASE_URL}/pay/cancelPlan`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  return response.json();
};
